<script>
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import FormInput from 'v3/components/Form/Input.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';

export default {
  components: {
    NextButton,
    FormInput,
    RadioCard,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    selectedAppData: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close'],
  setup() {
    return { v$: useVuelidate() };
  },
  validations: {
    app: {
      title: { required },
      content: {
        type: { required },
        url: { required, url },
      },
    },
  },
  data() {
    return {
      isLoading: false,
      app: {
        title: '',
        // Onde o app aparece, e a escolha e EXCLUSIVA: barra lateral OU aba
        // dentro da conversa, nunca os dois. `false` mantem o comportamento de
        // sempre, entao todo app ja existente segue como aba.
        showInNavigation: false,
        content: {
          type: 'frame',
          url: '',
        },
      },
    };
  },
  computed: {
    header() {
      return this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.HEADER`);
    },
    submitButtonLabel() {
      return this.$t(
        `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.FORM_SUBMIT`
      );
    },
  },
  mounted() {
    if (this.mode === 'UPDATE' && this.selectedAppData) {
      this.app.title = this.selectedAppData.title;
      this.app.content = this.selectedAppData.content[0];
      this.app.showInNavigation = Boolean(
        this.selectedAppData.show_in_navigation
      );
    }
  },
  methods: {
    setPlacement(placement) {
      this.app.showInNavigation = placement === 'sidebar';
    },
    closeModal() {
      // Reset the data once closed
      this.app = {
        title: '',
        showInNavigation: false,
        content: { type: 'frame', url: '' },
      };
      this.$emit('close');
    },
    async submit() {
      try {
        this.v$.$touch();
        if (this.v$.$invalid) {
          return;
        }

        const action = this.mode.toLowerCase();
        const payload = {
          title: this.app.title,
          show_in_navigation: this.app.showInNavigation,
          content: [this.app.content],
        };

        if (action === 'update') {
          payload.id = this.selectedAppData.id;
        }

        this.isLoading = true;
        await this.$store.dispatch(`dashboardApps/${action}`, payload);
        useAlert(
          this.$t(
            `INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_SUCCESS`
          )
        );
        this.closeModal();
      } catch (err) {
        useAlert(
          this.$t(`INTEGRATION_SETTINGS.DASHBOARD_APPS.${this.mode}.API_ERROR`)
        );
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="closeModal">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="header" />
      <form class="w-full" @submit.prevent="submit">
        <FormInput
          v-model="app.title"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_PLACEHOLDER')
          "
          :has-error="v$.app.title.$error"
          :error-message="
            v$.app.title.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.TITLE_ERROR')
              : ''
          "
          data-testid="app-title"
          @input="v$.app.title.$touch"
          @blur="v$.app.title.$touch"
        />
        <FormInput
          v-model="app.content.url"
          class="w-full"
          :label="$t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_LABEL')"
          :placeholder="
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_PLACEHOLDER')
          "
          :has-error="v$.app.content.url.$error"
          :error-message="
            v$.app.content.url.$error
              ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.URL_ERROR')
              : ''
          "
          data-testid="app-url"
          @input="v$.app.content.url.$touch"
          @blur="v$.app.content.url.$touch"
        />
        <div class="flex flex-col w-full gap-2 mb-4">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_LABEL') }}
          </label>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <RadioCard
              id="dashboard-app-placement-conversation"
              name="dashboard-app-placement"
              :label="
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_CONVERSATION_LABEL'
                )
              "
              :description="
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_CONVERSATION_DESCRIPTION'
                )
              "
              :is-active="!app.showInNavigation"
              @select="setPlacement('conversation')"
            />
            <RadioCard
              id="dashboard-app-placement-sidebar"
              name="dashboard-app-placement"
              :label="
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_SIDEBAR_LABEL'
                )
              "
              :description="
                $t(
                  'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.PLACEMENT_SIDEBAR_DESCRIPTION'
                )
              "
              :is-active="app.showInNavigation"
              @select="setPlacement('sidebar')"
            />
          </div>
        </div>
        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="
              $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.CREATE.FORM_CANCEL')
            "
            @click.prevent="closeModal"
          />
          <NextButton
            type="submit"
            :label="submitButtonLabel"
            :disabled="v$.$invalid"
            :is-loading="isLoading"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
